import { useEffect, useState } from "react";
import type { EventSummary } from "./api.js";

/**
 * Petites briques partagées par les écrans : formats d'affichage, détection du pointeur, et
 * le champ éditable au clic. Rien ici ne connaît d'écran en particulier — c'est le critère
 * pour qu'un utilitaire ait sa place dans ce fichier plutôt que dans celui de son écran.
 */
export function dateLabel(e: EventSummary): string {
  return e.dateApprox ?? "";
}

/**
 * Vrai quand le pointeur ne survole pas (doigt). Sert à choisir un geste, pas une
 * largeur d'écran : une tablette large n'a pas de survol non plus.
 */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(() => window.matchMedia("(hover: none)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(hover: none)");
    const onChange = (): void => setCoarse(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return coarse;
}

/**
 * Un « brouillon » = un groupe constitué mais pas encore nommé. C'est un album
 * comme les autres (une seule hiérarchie, cf. DESIGN §3.2), simplement sans titre :
 * il peut donc déjà recevoir de la voix en session, et le nommer se réduit à
 * éditer son titre.
 */
export function isDraft(e: EventSummary): boolean {
  return !e.title.trim();
}

export function fmtWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Champ éditable en place : on clique sur le texte, on tape, Entrée valide.
 * Échap annule, et cliquer ailleurs enregistre (rien ne se perd par mégarde).
 */
export function InlineField({
  value,
  placeholder,
  className,
  inputClassName,
  onSave,
  testId,
}: {
  value: string;
  placeholder: string;
  className: string;
  inputClassName: string;
  onSave: (v: string) => Promise<void>;
  testId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };
  const commit = async () => {
    if (saving) return;
    setEditing(false);
    if (draft.trim() === value.trim()) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        className={inputClassName}
        value={draft}
        placeholder={placeholder}
        autoFocus
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") setEditing(false);
        }}
        data-testid={`${testId}-in`}
      />
    );
  }

  return (
    <button
      className={className}
      onClick={start}
      title="Cliquer pour modifier"
      data-testid={testId}
    >
      {value || <span className="editable__ph">{placeholder}</span>}
    </button>
  );
}

/** Les descriptions sont longues : on n'en montre que le début dans l'historique. */
export function ellipsis(s: string | null, max = 80): string {
  if (s === null) return "∅";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}
